import { Module, MutationTree, ActionTree } from 'vuex';
import { SurveyStateInterface } from '../interfaces/surveyState';
import { RootStateInterface } from '@/store/rootState';
import { SurveyApi } from '../api/survey';
import { Survey, SurveyAnswers } from '../interfaces/survey';

export const state: SurveyStateInterface = {
  currentSurvey: null,
  surveysQueue: [],
  currentSurveyIndex: 0,
  loading: false,
  error: null,
};

export const mutations: MutationTree<SurveyStateInterface> = {
  SET_SURVEY(st: SurveyStateInterface, payload: Survey | Survey[] | null) {
    // Handle array of surveys
    if (Array.isArray(payload)) {
      st.surveysQueue = payload;
      st.currentSurveyIndex = 0;
      st.currentSurvey = payload.length > 0 ? payload[0] : null;
      console.log('SurveyStore - SET_SURVEY (array):', {
        queueLength: st.surveysQueue.length,
        currentIndex: st.currentSurveyIndex,
        currentSurveyId: st.currentSurvey?.surveyQuestionId,
        allIds: st.surveysQueue.map((s: Survey) => s.surveyQuestionId),
      });
    } else {
      // Handle single survey (backward compatibility) or null
      st.currentSurvey = payload;
      st.surveysQueue = payload ? [payload] : [];
      st.currentSurveyIndex = payload ? 0 : 0;
      console.log('SurveyStore - SET_SURVEY (single):', {
        queueLength: st.surveysQueue.length,
        currentIndex: st.currentSurveyIndex,
        currentSurveyId: st.currentSurvey?.surveyQuestionId,
      });
    }
  },
  NEXT_SURVEY(st: SurveyStateInterface) {
    if (st.currentSurveyIndex < st.surveysQueue.length - 1) {
      st.currentSurveyIndex += 1;
      st.currentSurvey = st.surveysQueue[st.currentSurveyIndex];
    } else {
      // No more surveys
      st.currentSurvey = null;
      st.surveysQueue = [];
      st.currentSurveyIndex = 0;
    }
  },
  CLEAR_SURVEYS(st: SurveyStateInterface) {
    st.surveysQueue = [];
    st.currentSurveyIndex = 0;
    st.currentSurvey = null;
  },
  SET_LOADING(st: SurveyStateInterface, payload: boolean) {
    st.loading = payload;
  },
  SET_ERROR(st: SurveyStateInterface, payload: string | null) {
    st.error = payload;
  },
  CLEAR_SURVEY(st: SurveyStateInterface) {
    st.currentSurvey = null;
    st.error = null;
  },
};

export const actions: ActionTree<SurveyStateInterface, RootStateInterface> = {
  async fetchAvailableSurvey({ commit }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const api = new SurveyApi();
      const response = await api.getAvailableSurvey();

      if (response.success && response.data) {
        // response.data is now an array of Survey objects
        const surveys = Array.isArray(response.data) ? response.data : [response.data];
        console.log('SurveyStore - fetchAvailableSurvey - Setting surveys:', {
          count: surveys.length,
          surveyIds: surveys.map((s: Survey) => s.surveyQuestionId),
        });
        commit('SET_SURVEY', surveys);
        return surveys;
      }

      // No survey available is not an error
      // IMPORTANT: Don't clear existing queue if we already have surveys queued
      // Only clear if queue is empty (user might be progressing through existing queue)
      const currentState = state;
      if (currentState.surveysQueue.length === 0) {
        commit('SET_SURVEY', null);
      } else {
        console.log('SurveyStore - fetchAvailableSurvey - No new surveys, but keeping existing queue:', {
          queueLength: currentState.surveysQueue.length,
          currentIndex: currentState.currentSurveyIndex,
        });
      }
      return null;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load survey';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  nextSurvey({ commit, state }) {
    console.log('SurveyStore - nextSurvey - Before:', {
      currentIndex: state.currentSurveyIndex,
      queueLength: state.surveysQueue.length,
      hasMore: state.currentSurveyIndex < state.surveysQueue.length - 1,
    });
    
    // Check if there are more surveys in the queue
    if (state.currentSurveyIndex < state.surveysQueue.length - 1) {
      // Move to next survey
      commit('NEXT_SURVEY');
      
      // Get the updated state after mutation
      const updatedState = state;
      console.log('SurveyStore - nextSurvey - After NEXT_SURVEY mutation:', {
        currentIndex: updatedState.currentSurveyIndex,
        currentSurveyId: updatedState.currentSurvey?.surveyQuestionId,
        queueLength: updatedState.surveysQueue.length,
      });
      
      // Return the updated current survey (after mutation)
      return updatedState.currentSurvey;
    }
    
    // No more surveys
    console.log('SurveyStore - nextSurvey - No more surveys, clearing');
    commit('CLEAR_SURVEYS');
    return null;
  },

  clearSurveys({ commit }) {
    commit('CLEAR_SURVEYS');
  },

  async submitSurvey({ commit, state }, payload: SurveyAnswers | { answers: SurveyAnswers; isAnonymous?: boolean | null }) {
    if (!state.currentSurvey) {
      throw new Error('No survey available');
    }

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const api = new SurveyApi();
      const answers = (payload as any).answers !== undefined ? (payload as any).answers : payload;
      const isAnonymous = (payload as any).answers !== undefined ? (payload as any).isAnonymous : undefined;
      const response = await api.submitSurveyResponse(
        state.currentSurvey.surveyQuestionId,
        answers,
        isAnonymous,
      );

      if (response.success) {
        // Don't clear the survey or queue here - let the modal handle progression to next survey
        // The queue should remain intact so we can move to the next survey
        // Only clear if there are no more surveys in the queue
        // The modal will call nextSurvey() which will handle clearing if needed
        console.log('SurveyStore - submitSurvey - Survey submitted, queue state:', {
          currentIndex: state.currentSurveyIndex,
          queueLength: state.surveysQueue.length,
          hasMore: state.currentSurveyIndex < state.surveysQueue.length - 1,
        });
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to submit survey');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit survey';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async markSurveysShown({ commit, state }) {
    // Mark all surveys in the queue as shown
    if (!state.surveysQueue || state.surveysQueue.length === 0) {
      return;
    }

    try {
      const api = new SurveyApi();
      // Mark all surveys as shown
      await Promise.all(
        state.surveysQueue.map((survey: Survey) =>
          api.markSurveyShown(survey.surveyQuestionId).catch(error => {
            console.error('Failed to mark survey as shown:', survey.surveyQuestionId, error);
          })
        )
      );
      console.log('SurveyStore - Marked surveys as shown:', {
        surveyIds: state.surveysQueue.map((s: Survey) => s.surveyQuestionId),
      });
    } catch (error: any) {
      console.error('Error marking surveys as shown:', error);
      // Don't throw - this shouldn't block the user from seeing surveys
    }
  },

  async dismissSurvey({ commit, state }) {
    if (!state.currentSurvey) {
      return;
    }

    const surveyQuestionId = state.currentSurvey.surveyQuestionId;

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const api = new SurveyApi();
      await api.dismissSurvey(surveyQuestionId);
      // Don't clear the survey here - let the modal handle progression to next survey
      // Only clear if there are no more surveys in the queue
      // The modal will call nextSurvey() which will handle clearing if needed
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to dismiss survey';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
};

const namespaced: boolean = true;

export const survey: Module<SurveyStateInterface, RootStateInterface> = {
  namespaced,
  state,
  mutations,
  actions,
};
